/**
 * UC3 — Agricultural Productivity Diagnosis, maize (design §3.3, Amendment 1).
 *
 * A stepper (`Stepper.tsx`) over machinery this codebase already has: the geography/scope
 * cascade, the catalogue's own raster epochs, and the `conditions` engine tool (Task 5)
 * submitted through the same `useToolRun` every §F6 dock form uses. Local state carries the
 * reader's choices — none of it is permalinked, the same rule `intel.*` is already under
 * (design §6.3) — seeded once from the store's own country/scope so a reader who has
 * already filtered the map to a country or a watershed opens the stepper already pointed
 * there, without this stepper ever writing back to those global controls (the same one-way
 * seeding `Change.tsx`'s `initialExtent` uses for its own extent pick).
 *
 * Amendment 1 §2.3/§3.3: the step list itself comes from `GET uc3/definition`'s own
 * `definition.steps` (order, ids, kinds) rather than a hardcoded array, so a future
 * definition edit (reordering, adding/removing a step) needs no frontend change — only the
 * `aeti` step is folded into the rainfall step as its own optional toggle (`#uc3Aeti`)
 * rather than consuming a step number of its own. `#uc3Step-<n>` still numbers the steps
 * 1..N in the order the definition lists them.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToolRun, type ConditionClause, type ConditionsResult } from '../../lib/analysis';
import {
  fetchUnitOptions,
  fetchValues,
  type IntelDiagnosis,
  type IntelSeries,
  type IntelStepDefinitionRow,
  type IntelUc3Crop,
} from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { COUNTRY_NAMES, useAppStore, WATERSHED_LEVEL, type Country } from '../../lib/state';
import { epochDate, epochsForYear } from '../map/epochs';
import { useAnalysisLayers } from '../analysis/pickers';
import { Caveats } from './Caveats';
import { ConditionBuilder } from './ConditionBuilder';
import { Insight } from './Insight';
import { Stepper, type StepperStep } from './Stepper';
import { HistogramChart } from './charts/HistogramChart';
import { RankedBarChart } from './charts/RankedBarChart';
import { SeriesChart } from './charts/SeriesChart';
import { useIntelDefinition, useIntelDiagnosis, useIntelSeries, useIntelUc3Config } from './api';
import { useUnitLink } from './useUnitLink';

/** The four real countries UC3 can run against — `conditions.py::_country` refuses "ALL". */
const UC3_COUNTRIES = (Object.keys(COUNTRY_NAMES) as Country[]).filter((c) => c !== 'ALL');

const SOIL_LAYERS = ['soc', 'sgn', 'sgcec', 'ph'] as const;
type SoilLayer = (typeof SOIL_LAYERS)[number];

const DIAGNOSIS_FACTORS = ['ndvi_peak', 'rain', 'soc'] as const;

/** `uc3/diagnosis`'s own factor units — presentation standard §1's "numbers with unit". */
const DIAGNOSIS_UNITS: Record<'yield' | 'modelled_yield' | (typeof DIAGNOSIS_FACTORS)[number], string> = {
  yield: 'kg/ha',
  modelled_yield: 't/ha',
  ndvi_peak: 'NDVI',
  rain: 'mm',
  soc: 'g/kg',
};

/**
 * GAEZ round: `uc3/definition`'s own `definition.crops`/`uc3/config`'s own `crops` — kept in
 * sync server-side (Task 2's own report) — used before either has resolved, the same role
 * `FALLBACK_STEPS` plays for the step list.
 */
const FALLBACK_CROPS: IntelUc3Crop[] = [
  { id: 'maize', label: { en: 'Maize', fr: 'Maïs' }, survey: 'mb_yield_maize', mask: 'mask_maize', gaez: 'gaez_yld_maize' },
  { id: 'rice', label: { en: 'Rice', fr: 'Riz' }, survey: 'mb_yield_rice', mask: 'mask_rice', gaez: 'gaez_yld_rice' },
  { id: 'soy', label: { en: 'Soybean', fr: 'Soja' }, survey: 'mb_yield_soybean', mask: 'mask_soy', gaez: 'gaez_yld_soy' },
  { id: 'sorghum', label: { en: 'Sorghum', fr: 'Sorgho' }, survey: 'mb_yield_sorghum', mask: null, gaez: 'gaez_yld_sorghum' },
  { id: 'millet', label: { en: 'Millet', fr: 'Mil' }, survey: 'mb_yield_millet', mask: null, gaez: 'gaez_yld_millet' },
];

/**
 * Fix round 1: a defensive display filter — no ρ/correlation term reaches the reader even
 * from a stale cache or a payload a future backend round mis-shapes, on top of the design
 * guarantee that the server never sends one. Matches the same four terms
 * `vocabulary.test.ts` sweeps the locale files for.
 */
function hasForbiddenStatsTerm(text: string): boolean {
  if (text.includes('ρ')) return true;
  return /\b(rho|correlation|corrélation)\b/i.test(text);
}

/** Drops any statement line carrying a forbidden term, warning once per dropped line. */
function safeStatements(lines: string[] | undefined): string[] {
  if (!lines) return [];
  return lines.filter((line) => {
    if (hasForbiddenStatsTerm(line)) {
      console.warn('[uc3] dropped a diagnosis statement carrying a forbidden correlation term', line);
      return false;
    }
    return true;
  });
}

/** Same filter for the single insight paragraph, per language, falling back to the empty state. */
function safeInsightText(text: string | undefined, fallback: string): string {
  if (!text) return fallback;
  if (hasForbiddenStatsTerm(text)) {
    console.warn('[uc3] dropped a diagnosis insight carrying a forbidden correlation term', text);
    return fallback;
  }
  return text;
}

/**
 * The stepper's own fallback order — used only while `uc3/definition` has not resolved yet,
 * so the pane still renders something on first paint. Identical in shape to the live
 * `uc3/definition.steps` (`aeti` folded into `rainfall`, `results` last).
 */
const FALLBACK_STEPS: IntelStepDefinitionRow[] = [
  { id: 'geography', kind: 'geography', binding: { levels: ['admin0', 'watershed'] } },
  { id: 'crop', kind: 'crop', binding: {} },
  { id: 'year', kind: 'year', binding: {} },
  { id: 'indicator', kind: 'indicator', binding: { indicator: 'mb_yield_maize' } },
  { id: 'ndvi', kind: 'raster', binding: { layer: 'ndvi_terra' } },
  {
    id: 'rainfall',
    kind: 'raster',
    binding: { layer: 'rainfall', policy: 'dekads_else_annual', datasets: { dekad: 'chirps', annual: 'chirpsy' } },
  },
  { id: 'aeti', kind: 'raster', binding: { layer: 'aeti', period: 2025 }, optional: true },
  { id: 'soil', kind: 'soil_selector', binding: { layers: ['soc', 'sgcec', 'ph'] } },
  { id: 'conditions', kind: 'conditions', binding: {} },
  { id: 'results', kind: 'results', binding: { ranked: true, correlations: false } },
];

function uniqSorted(values: number[]): number[] {
  return [...new Set(values.filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
}

function yearOf(date: string): number {
  return Number(date.slice(0, 4));
}

function langOf(language: string): 'en' | 'fr' {
  return language === 'fr' ? 'fr' : 'en';
}

export function Uc3() {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const step = useAppStore((s) => s.intel.step);
  const setStep = useAppStore((s) => s.setIntelStep);
  const storeCountry = useAppStore((s) => s.country);
  const storeScope = useAppStore((s) => s.scope);
  const swipe = useAppStore((s) => s.swipe);
  const setSwipe = useAppStore((s) => s.setSwipe);
  const layerOpacity = useAppStore((s) => s.layerOpacity);
  const setLayerOpacity = useAppStore((s) => s.setLayerOpacity);
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);
  const setEpoch = useAppStore((s) => s.setEpoch);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const setIntelLayerToggled = useAppStore((s) => s.setIntelLayerToggled);

  useEffect(() => {
    if (step === 0) setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const active = step || 1;

  const definitionQuery = useIntelDefinition('uc3');
  // `aeti` is a toggle inside the rainfall step, not a step of its own — see module doc.
  const stepDefs = useMemo(
    () => (definitionQuery.data?.definition.steps ?? FALLBACK_STEPS).filter((s) => s.id !== 'aeti'),
    [definitionQuery.data],
  );
  const aetiDef = (definitionQuery.data?.definition.steps ?? FALLBACK_STEPS).find((s) => s.id === 'aeti');
  const stepNumberOf = (id: string): number => {
    const i = stepDefs.findIndex((s) => s.id === id);
    return i === -1 ? 0 : i + 1;
  };
  const rainfallDef = stepDefs.find((s) => s.id === 'rainfall');

  const [country, setCountry] = useState<Country>(() =>
    storeCountry !== 'ALL' ? storeCountry : UC3_COUNTRIES[0]!,
  );
  const [scopeUnit, setScopeUnit] = useState<string>(() =>
    storeScope?.unitType === 'watershed' ? storeScope.unitId : '',
  );

  const watersheds = useQuery({
    queryKey: ['uc3-watersheds', country],
    queryFn: () => fetchUnitOptions('watershed', null, country as Country, null, false),
  });

  const config = useIntelUc3Config();
  /**
   * GAEZ round: `definition.crops` is the ruling's own source for the crop select — falls
   * back to `uc3/config`'s own `crops` (kept in sync server-side) while the definition has
   * not resolved yet, then to the hardcoded fallback.
   */
  const crops = definitionQuery.data?.definition.crops ?? config.data?.crops ?? FALLBACK_CROPS;
  const [crop, setCrop] = useState<string>('maize');
  useEffect(() => {
    if (crops.length && !crops.some((c) => c.id === crop)) setCrop(crops[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionQuery.data, config.data]);
  const cropDef = crops.find((c) => c.id === crop) ?? crops[0];
  const cropLabel = cropDef ? cropDef.label[lang] : crop;

  const layers = useAnalysisLayers();
  const ndviSeries = layers.find((l) => l.id === 'ndvi_terra') ?? null;
  const ndviYears = useMemo(
    () => uniqSorted((ndviSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [ndviSeries],
  );

  /* ---- rainfall step: dekad (`chirps`) when the season's dekads exist, else annual (`chirpsy`) ---- */
  const dekadLayerId = rainfallDef?.binding.datasets?.dekad ?? 'chirps';
  const annualLayerId = rainfallDef?.binding.datasets?.annual ?? 'chirpsy';
  const dekadSeries = layers.find((l) => l.id === dekadLayerId) ?? null;
  const annualSeries = layers.find((l) => l.id === annualLayerId) ?? null;
  const dekadYears = useMemo(
    () => uniqSorted((dekadSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [dekadSeries],
  );
  const annualYears = useMemo(
    () => uniqSorted((annualSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [annualSeries],
  );

  /**
   * Years both `ndvi_terra` and the annual rainfall layer can answer for — falling back to
   * `ndvi_terra` alone when the rainfall archive has published none yet or the two series
   * simply do not overlap, rather than offering the reader an empty select.
   */
  const availableYears = useMemo(() => {
    if (!annualYears.length) return ndviYears;
    const intersection = ndviYears.filter((y) => annualYears.includes(y));
    return intersection.length ? intersection : ndviYears;
  }, [ndviYears, annualYears]);
  const [year, setYear] = useState<number | null>(null);
  useEffect(() => {
    if (availableYears.length && (year === null || !availableYears.includes(year))) {
      setYear(availableYears[availableYears.length - 1]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears.join(',')]);

  // "season dekads when the archive holds the season, else the annual layer" — approximated
  // here as "the dekad layer has published at least one epoch for the chosen year", the same
  // year-level granularity every other epoch check in this stepper already uses.
  const usesDekads = year !== null && dekadYears.includes(year);
  const rainfallLayerId = usesDekads ? dekadLayerId : annualLayerId;
  const rainfallCaptionKey = usesDekads ? 'intel.uc3.rainfallCaptionDekads' : 'intel.uc3.rainfallCaptionAnnual';
  const [aetiOn, setAetiOn] = useState(false);
  useEffect(() => {
    if (active !== stepNumberOf('rainfall')) setAetiOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const [soilLayer, setSoilLayer] = useState<SoilLayer>('soc');

  /* ---- indicator step: productivity choropleth — the existing indicator choropleth engine ---- */
  const indicatorStepNum = stepNumberOf('indicator');
  const surveyIndicator = cropDef?.survey ?? 'mb_yield_maize';
  const yieldValues = useQuery({
    queryKey: ['uc3-yield', country, year, surveyIndicator],
    queryFn: () => fetchValues(surveyIndicator, WATERSHED_LEVEL, year ?? 0, country as Country),
    enabled: active === indicatorStepNum && year !== null,
  });
  useEffect(() => {
    if (active !== indicatorStepNum || !yieldValues.data) return;
    const values = Object.fromEntries(
      yieldValues.data.values
        .filter((v) => v.value !== null)
        .map((v) => [v.unit_id, v.value as number]),
    );
    addResultLayer(
      {
        id: 'intel:uc3:productivity',
        kind: 'choropleth',
        label: t('intel.uc3.productivityLabel', { crop: cropLabel }),
        values,
        ramp: 'green',
        method: 'quantile',
        k: 3,
        unit_label: 'kg/ha',
      },
      { jobId: `uc3-yield-${country}-${year}-${crop}`, tool: 'uc3' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, yieldValues.data]);

  /**
   * Fix round 1: the vegetation/rainfall/soil steps each switch on a raster the stepper
   * itself chose, not the reader through the ordinary Layers pane — so each one has to be
   * recorded in `intel.layersToggled` (`setIntelLayerToggled`) the same way `Uc1.tsx`'s
   * `Underlying.tsx`'s toggles already are, or `resetIntel`/
   * `setIntelUseCase` would leave it running after a reset or a use-case switch. One id is
   * ever "this stepper's own raster" at a time (these three steps are mutually exclusive),
   * so a single ref tracks it and `activateRaster` releases whichever one that was — on the
   * *next* raster this stepper turns on, on leaving those steps altogether (`id === null`
   * below), and on unmount.
   */
  const introducedRaster = useRef<string | null>(null);
  const activateRaster = (id: string | null) => {
    const prev = introducedRaster.current;
    if (prev === id) return;
    // `setActiveRaster(null)` is the actual on/off switch (`layersOn`) — called
    // unconditionally on every real change, including "turn it off" (`id === null`), which
    // a first version of this only did when `id` was truthy and left `layersOn` carrying the
    // previous raster after leaving these steps or unmounting mid-step.
    if (prev) setIntelLayerToggled(prev, false);
    setActiveRaster(id);
    if (id) setIntelLayerToggled(id, true);
    introducedRaster.current = id;
  };

  const ndviStepNum = stepNumberOf('ndvi');
  const rainfallStepNum = stepNumberOf('rainfall');
  const soilStepNum = stepNumberOf('soil');

  /* ---- vegetation / rainfall / soil rasters ---- */
  useEffect(() => {
    if (active === ndviStepNum) {
      activateRaster('ndvi_terra');
      if (ndviSeries) {
        const inWindow = epochsForYear(ndviSeries, year);
        const target =
          inWindow[inWindow.length - 1] ?? ndviSeries.epochs?.[ndviSeries.epochs.length - 1];
        if (target) setEpoch('ndvi_terra', epochDate(target));
      }
    } else if (active === rainfallStepNum) {
      activateRaster(aetiOn ? 'aeti' : rainfallLayerId);
    } else if (active === soilStepNum) {
      activateRaster(soilLayer);
    } else {
      activateRaster(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, soilLayer, aetiOn, rainfallLayerId]);

  // A reader who closes the dock (or switches use case) mid-step still has to have the
  // raster released — the effect above only fires on its own dependency change, never on an
  // unmount that carries no change with it.
  useEffect(() => () => activateRaster(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- conditions step: the condition builder ---- */
  const run = useToolRun<ConditionsResult>('conditions');
  const onRunConditions = (clauses: ConditionClause[]) => {
    run.run({ clauses, country, scope: scopeUnit || null, year, crop });
  };
  const resultsStepNum = stepNumberOf('results');
  useEffect(() => {
    if (!run.result) return;
    // Fix round 1: `conditions` names its own layers `conditions:<job_id>[:choropleth]` —
    // an id that never starts with `intel:`, so `resetIntel`/`setIntelUseCase`'s own
    // `results.filter((r) => !r.id.startsWith('intel:'))` cleanup (`lib/state.ts`) could
    // never see it. Re-namespaced under `intel:uc3:` before it ever reaches the store, the
    // same convention every other intel result layer (`intel:uc1:choropleth`,
    // `intel:{uc}:selected`, …) already uses, so a use-case switch or Reset removes it too.
    run.result.result_layers.forEach((spec) =>
      addResultLayer(
        { ...spec, id: `intel:uc3:${spec.id}` },
        { jobId: run.jobId ?? spec.id, tool: 'conditions', label: spec.label },
      ),
    );
    setStep(resultsStepNum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.result]);

  /**
   * Fix round 1: the results step's selected unit is `useUnitLink('uc3')`'s own `unit` — the
   * same map-click-selects-it wiring `Uc1`/`Uc2` already use — rather than local state of
   * its own, so clicking a highlighted unit *on the map* (a boundary the `conditions`
   * choropleth painted) reaches this pane exactly as picking one from a chart does.
   */
  const { unit: selectedUnit, pick: pickUnit } = useUnitLink('uc3');
  /**
   * Fix wave (launch intelligence, F3c): `uc3/diagnosis` is the whole-programme ranked view
   * design §3.3 describes, not the stepper's own country/scope pick, which only scopes the
   * `conditions` job and its choropleth. Every one of this dev stack's four countries has
   * 2-3 watersheds on its own — scoping the diagnosis to one country would starve the
   * ranking, regardless of which country or watershed the reader picked in step 1.
   */
  const diagnosis = useIntelDiagnosis(crop, year, null);
  const series = useIntelSeries(selectedUnit, year);
  const onSelectUnit = (id: string) => {
    const label =
      run.result?.by_unit.find((u) => u.unit_id === id)?.name ??
      diagnosis.data?.ranked.find((p) => p.unit_id === id)?.name;
    void pickUnit(id, label);
  };
  useEffect(() => {
    if (!run.result?.by_unit?.length || selectedUnit) return;
    const top = [...run.result.by_unit].sort((a, b) => (b.share ?? 0) - (a.share ?? 0))[0];
    if (top) void pickUnit(top.unit_id, top.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.result]);

  const countryName = t(`country.${country}`, { defaultValue: COUNTRY_NAMES[country] });
  const scopeName = watersheds.data?.find((u) => u.id === scopeUnit)?.name;

  /* ---- one body renderer per step kind, keyed by the definition's own step id ---- */
  const bodyFor = (def: IntelStepDefinitionRow, n: number): { title: string; summary: ReactNode; body: ReactNode } => {
    switch (def.id) {
      case 'geography':
        return {
          title: t('intel.uc3.step.geography'),
          summary: `${countryName}${scopeName ? ` — ${scopeName}` : ''}`,
          body: (
            <div className="ad-checks">
              <label>
                {t('indicators.levelCountry')}
                <select
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value as Country);
                    setScopeUnit('');
                  }}
                >
                  {UC3_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`country.${c}`, { defaultValue: COUNTRY_NAMES[c] })}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('intel.uc3.watershedLabel')}
                <select value={scopeUnit} onChange={(e) => setScopeUnit(e.target.value)}>
                  <option value="">{t('intel.uc3.wholeCountry')}</option>
                  {(watersheds.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'crop':
        return {
          title: t('intel.uc3.step.crop'),
          summary: cropLabel,
          body: (
            <div className="ad-checks">
              <select id="uc3Crop" value={crop} onChange={(e) => setCrop(e.target.value)}>
                {crops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label[lang] ?? c.id}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'year':
        return {
          title: t('intel.uc3.step.year'),
          summary: year ?? '—',
          body: (
            <div className="ad-checks">
              <select value={year ?? ''} onChange={(e) => setYear(Number(e.target.value))}>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'indicator':
        return {
          title: t('intel.uc3.step.productivity'),
          summary: t('intel.uc3.productivityLabel', { crop: cropLabel }),
          body: (
            <div>
              <p className="note">{t('intel.uc3.productivityNote')}</p>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'ndvi':
        return {
          title: t('intel.uc3.step.vegetation'),
          summary: t('intel.uc3.step.vegetation'),
          body: (
            <div className="ad-checks">
              <p className="note">{t('intel.uc3.vegetationNote')}</p>
              <button
                type="button"
                id="uc3Swipe"
                className={`btn ghost sm${swipe ? ' on' : ''}`}
                onClick={() => setSwipe(!swipe)}
              >
                {t('intel.uc3.toggleSwipe')}
              </button>
              <label>
                {t('intel.uc3.opacityLabel')}
                <input
                  id="uc3Opacity"
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={layerOpacity.ndvi_terra ?? 1}
                  onChange={(e) => setLayerOpacity('ndvi_terra', Number(e.target.value))}
                />
              </label>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'rainfall':
        return {
          title: t('intel.uc3.step.rainfall'),
          summary: t('intel.uc3.step.rainfall'),
          body: (
            <div className="ad-checks">
              <p className="note">{t(rainfallCaptionKey)}</p>
              {aetiDef ? (
                <label>
                  <input
                    type="checkbox"
                    id="uc3Aeti"
                    checked={aetiOn}
                    onChange={(e) => setAetiOn(e.target.checked)}
                  />
                  {t('intel.uc3.aetiLabel')}
                </label>
              ) : null}
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'soil':
        return {
          title: t('intel.uc3.step.soil'),
          summary: t(`intel.uc3.layer.${soilLayer}`),
          body: (
            <div className="ad-checks">
              <select id="uc3Soil" value={soilLayer} onChange={(e) => setSoilLayer(e.target.value as SoilLayer)}>
                {SOIL_LAYERS.map((l) => (
                  <option key={l} value={l}>
                    {t(`intel.uc3.layer.${l}`)}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'conditions':
        return {
          title: t('intel.uc3.step.analyse'),
          summary: t('intel.uc3.analyseSummary'),
          body: (
            <ConditionBuilder
              config={config.data}
              crop={crop}
              cropLabel={cropLabel}
              run={run}
              onRun={onRunConditions}
            />
          ),
        };
      case 'results':
      default:
        return {
          title: t('intel.uc3.step.results'),
          summary: undefined,
          body: run.result ? (
            <Uc3Results
              result={run.result}
              diagnosis={diagnosis.data}
              series={series.data}
              selectedUnit={selectedUnit}
              onSelectUnit={onSelectUnit}
              lang={lang}
              cropLabel={cropLabel}
            />
          ) : (
            <p className="note">{t('intel.uc3.noResultYet')}</p>
          ),
        };
    }
  };

  const steps: StepperStep[] = stepDefs.map((def, i) => {
    const n = i + 1;
    const { title, summary, body } = bodyFor(def, n);
    return { id: n, title, summary, body };
  });

  return (
    <div className="intel-uc3">
      <Stepper active={active} onSelect={setStep} steps={steps} />
      <Caveats useCase="uc3" crop={crop} cropAreaRule={config.data?.crop_area_rule?.gaez_fallback ?? null} />
    </div>
  );
}

function Uc3Results({
  result,
  diagnosis,
  series,
  selectedUnit,
  onSelectUnit,
  lang,
  cropLabel,
}: {
  result: ConditionsResult;
  diagnosis?: IntelDiagnosis;
  series?: IntelSeries;
  selectedUnit: string | null;
  onSelectUnit: (id: string) => void;
  lang: 'en' | 'fr';
  cropLabel: string;
}) {
  const { t } = useTranslation();

  const histogramRows = result.by_unit.map((u) => ({
    id: u.unit_id,
    label: u.name,
    value: u.share == null ? null : u.share * 100,
  }));

  // Fix round 1: every read below is guarded — a stale cache entry, a partial payload, or
  // the pre-Task-4 `points`/`correlations` shape a test can still hand this component must
  // fall through to the designed empty state rather than throwing.
  const rankedRows = diagnosis?.ranked ?? [];
  const countryRows = diagnosis?.countries ?? [];
  const statementLines = safeStatements(diagnosis?.statements?.[lang]);
  const point = rankedRows.find((p) => p.unit_id === selectedUnit);
  const meanOf = (factor: (typeof DIAGNOSIS_FACTORS)[number] | 'yield'): number | null => {
    const vals = rankedRows.map((p) => p[factor]).filter((v): v is number => typeof v === 'number');
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  return (
    <div>
      <div className="result-box" id="uc3Stats">
        <h5>{t('intel.uc3.statsTitle')}</h5>
        <div className="result-pad">
          <table className="mini">
            <tbody>
              <tr>
                <td>{t('intel.uc3.stats.area')}</td>
                <td className="num">{fmtNum(result.area_ha, 0)} ha</td>
              </tr>
              <tr>
                <td>{t('intel.uc3.stats.share')}</td>
                <td className="num">
                  {result.share_of_ag_area != null ? `${fmtNum(result.share_of_ag_area * 100, 1)}%` : '—'}
                </td>
              </tr>
              <tr>
                <td>{t('intel.uc3.stats.units')}</td>
                <td className="num">{result.n_units_touched}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="result-box" id="uc3Histogram">
        <h5>{t('intel.uc3.histogramTitle')}</h5>
        <div className="result-pad">
          <HistogramChart
            rows={histogramRows}
            title={t('intel.uc3.histogramTitle')}
            selectedId={selectedUnit}
            onSelect={onSelectUnit}
          />
        </div>
      </div>

      {/*
       * Amendment 1 §3.3: no correlation coefficient anywhere — the ranked bar chart and the
       * server's own rank-based `statements[]` replace the old scatter/ρ block outright.
       * Always rendered in the results step, same "designed empty state, never a silently
       * missing chart" rule the old block followed.
       */}
      <div className="result-box" id="uc3Ranked">
        <h5>{t('intel.uc3.rankedTitle')}</h5>
        <div className="result-pad">
          {rankedRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <RankedBarChart
                rows={rankedRows}
                season={diagnosis?.season ?? ''}
                year={diagnosis?.year ?? 0}
                yieldLabel={t('intel.charts.yield', { crop: cropLabel })}
                modelledLabel={t('intel.uc3.modelledColumnLabel')}
                ndviLabel={t('intel.charts.factor.ndvi_peak')}
                rainLabel={t('intel.charts.factor.rain')}
                socLabel={t('intel.charts.factor.soc')}
                yieldUnit={DIAGNOSIS_UNITS.yield}
                modelledUnit={DIAGNOSIS_UNITS.modelled_yield}
                ndviUnit={DIAGNOSIS_UNITS.ndvi_peak}
                rainUnit={DIAGNOSIS_UNITS.rain}
                socUnit={DIAGNOSIS_UNITS.soc}
                selectedId={selectedUnit}
                onSelect={onSelectUnit}
              />
              {statementLines.length ? (
                <ul id="uc3Statements" className="tbl-note">
                  {statementLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noRankedData')}</p>
          )}
        </div>
      </div>

      {/*
       * Amendment 1 fix wave (F12): the diagnosis payload's `countries[]` — the same four
       * columns as the ranked chart, one row per programme country instead of per watershed
       * (no `rank`) — shown as its own titled block right below the ranked chart. Same
       * "designed empty state, never a silently missing block" rule as `uc3Ranked`.
       */}
      <div className="result-box" id="uc3Countries">
        <h5>{t('intel.uc3.countryRollupTitle')}</h5>
        <div className="result-pad">
          {countryRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <table className="mini" id="uc3CountriesTable">
                <thead>
                  <tr>
                    <th>{t('intel.uc3.countryRollupCountry')}</th>
                    {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                      <th className="num" key={f}>
                        {t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })} ({DIAGNOSIS_UNITS[f]})
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {countryRows.map((c) => (
                    <tr key={c.unit_id}>
                      <td>{c.name}</td>
                      {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                        <td className="num" key={f}>
                          {typeof c[f] === 'number' ? fmtNum(c[f] as number, 1) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noCountryData')}</p>
          )}
        </div>
      </div>

      <div className="result-box" id="uc3Series">
        <h5>{t('intel.uc3.seriesTitle')}</h5>
        <div className="result-pad">
          {!selectedUnit ? (
            <p className="ad-status">{t('intel.uc3.noSeriesUnit')}</p>
          ) : series && series.ndvi.length === 0 && series.rain.length === 0 ? (
            <p className="ad-status">{t('intel.uc3.noSeriesData')}</p>
          ) : series ? (
            <SeriesChart
              ndvi={series.ndvi}
              rain={series.rain}
              ndviLabel={t('intel.charts.ndvi')}
              rainLabel={t('intel.charts.rain')}
            />
          ) : null}
        </div>
      </div>

      {diagnosis?.insight ? (
        <Insight
          insight={{
            en: safeInsightText(diagnosis.insight.en, t('intel.uc3.insightUnavailable')),
            fr: safeInsightText(diagnosis.insight.fr, t('intel.uc3.insightUnavailable', { lng: 'fr' })),
          }}
          limitations={diagnosis.limitations ?? []}
        />
      ) : null}

      {point ? (
        <div className="result-box" id="uc3UnitVsCountry">
          <h5>{point.name}</h5>
          <div className="result-pad">
            <table className="mini">
              <thead>
                <tr>
                  <th />
                  <th className="num">{point.name}</th>
                  <th className="num">{t('intel.uc3.countryMean')}</th>
                </tr>
              </thead>
              <tbody>
                {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                  <tr key={f}>
                    <td>{t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })}</td>
                    <td className="num">
                      {typeof point[f] === 'number' ? fmtNum(point[f] as number, 1) : '—'}
                    </td>
                    <td className="num">
                      {meanOf(f) != null ? fmtNum(meanOf(f) as number, 1) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Uc3;
